import { useCallback, useMemo, useReducer, useRef } from "react";

import { isEqual } from "lodash-es";

import {
  CellMap,
  copyRowCells,
  deleteCell,
  deleteRowCells,
  getCell,
  hasCell,
  setCell,
} from "src/cases/caseView/blocks/cellMap";
import {
  CellDisplayType,
  getAllKeys,
  MIN_STRING_CELL_HEIGHT,
} from "src/cases/caseView/blocks/cellTypes";
import {
  TableBlockMultiColumnRecord,
  TableBlockSingleRecord,
} from "src/cases/queries/casesTypes";

// Rows keep an id for the whole edit session: original rows are named after
// their index in the record the session started from, new rows get a counter.
// All per-cell state is keyed by row id, so row operations never shift it.
const getOriginalRowId = (index: number): string => `row:${index}`;
const NEW_ROW_ID_PREFIX = "new:";
const isNewRowId = (rowId: string | undefined): boolean =>
  rowId?.startsWith(NEW_ROW_ID_PREFIX) ?? false;
const deriveRowIds = (length: number): string[] =>
  Array.from({ length }, (_, index) => getOriginalRowId(index));

type Rows = TableBlockMultiColumnRecord;

type EditsState = {
  /** Snapshot taken on the first change; null derives from props so polling
   *  stays in sync while nothing is edited. */
  rows: Rows | null;
  rowIds: string[] | null;
  cellEdits: CellMap<unknown>;
  typeOverrides: CellMap<CellDisplayType>;
  /** Dragged string cell heights. Not saved, so they outlive the edits. */
  cellHeights: CellMap<number>;
};

type SnapshottedState = EditsState & { rows: Rows; rowIds: string[] };

export type CellHeights = EditsState["cellHeights"];

const INITIAL_STATE: EditsState = {
  rows: null,
  rowIds: null,
  cellEdits: new Map(),
  typeOverrides: new Map(),
  cellHeights: new Map(),
};

type EditsAction =
  | { type: "updateCell"; rowId: string; column: string; value: unknown }
  | { type: "addRow"; rowId: string }
  | { type: "duplicateRow"; sourceRowId: string; rowId: string }
  | {
      type: "insertRow";
      beforeRowId: string | null;
      rowId: string;
      row: TableBlockSingleRecord;
    }
  | { type: "deleteRow"; rowId: string }
  | {
      type: "setTypeOverride";
      rowId: string;
      column: string;
      displayType: CellDisplayType | null;
    }
  | { type: "setCellHeight"; rowId: string; column: string; height: number }
  | { type: "restoreCellHeights"; cellHeights: CellHeights }
  | { type: "discardEdits" }
  | { type: "commitEdits" };

/** The live record travels with each action so the reducer can snapshot it. */
type DispatchedAction = EditsAction & { base: Rows };

const isSnapshotted = (state: EditsState): state is SnapshottedState =>
  state.rows !== null && state.rowIds !== null;

const snapshot = (state: EditsState, base: Rows): SnapshottedState =>
  isSnapshotted(state)
    ? state
    : { ...state, rows: base, rowIds: deriveRowIds(base.length) };

/** Applies `changes`, returning the same state when nothing changed so React
 *  can skip the render. */
const patch = <S extends EditsState>(
  state: S,
  changes: Partial<EditsState>,
): EditsState =>
  Object.entries(changes).every(
    ([key, value]) => state[key as keyof EditsState] === value,
  )
    ? state
    : { ...state, ...changes };

const insertRowAt = (
  state: SnapshottedState,
  index: number,
  rowId: string,
  row: TableBlockSingleRecord,
): SnapshottedState => ({
  ...state,
  rows: state.rows.toSpliced(index, 0, row),
  rowIds: state.rowIds.toSpliced(index, 0, rowId),
});

const editsReducer = (
  state: EditsState,
  action: DispatchedAction,
): EditsState => {
  switch (action.type) {
    case "updateCell": {
      const { rowId, column, value, base } = action;
      const rows = state.rows ?? base;
      const index = (state.rowIds ?? deriveRowIds(rows.length)).indexOf(rowId);
      if (index === -1) return state;
      // Reverting to the original value only drops the edit; a real change
      // also snapshots the record so polling can't shift rows underneath.
      if (isEqual(value, rows[index][column])) {
        return patch(state, {
          cellEdits: deleteCell(state.cellEdits, rowId, column),
        });
      }
      const next = snapshot(state, base);
      return patch(next, {
        cellEdits: setCell(next.cellEdits, rowId, column, value),
      });
    }
    case "addRow": {
      const next = snapshot(state, action.base);
      const emptyRow = Object.fromEntries(
        getAllKeys(next.rows).map((key) => [key, null]),
      );
      return insertRowAt(next, next.rows.length, action.rowId, emptyRow);
    }
    case "duplicateRow": {
      const next = snapshot(state, action.base);
      const index = next.rowIds.indexOf(action.sourceRowId);
      if (index === -1) return next;
      const { sourceRowId, rowId } = action;
      const inserted = insertRowAt(next, index + 1, rowId, {
        ...next.rows[index],
      });
      return {
        ...inserted,
        cellEdits: copyRowCells(inserted.cellEdits, sourceRowId, rowId),
        typeOverrides: copyRowCells(inserted.typeOverrides, sourceRowId, rowId),
        cellHeights: copyRowCells(inserted.cellHeights, sourceRowId, rowId),
      };
    }
    case "insertRow": {
      const next = snapshot(state, action.base);
      const anchor = action.beforeRowId
        ? next.rowIds.indexOf(action.beforeRowId)
        : -1;
      const index = anchor === -1 ? next.rows.length : anchor;
      return insertRowAt(next, index, action.rowId, action.row);
    }
    case "deleteRow": {
      const next = snapshot(state, action.base);
      const index = next.rowIds.indexOf(action.rowId);
      if (index === -1) return next;
      const { rowId } = action;
      return {
        rows: next.rows.toSpliced(index, 1),
        rowIds: next.rowIds.toSpliced(index, 1),
        cellEdits: deleteRowCells(next.cellEdits, rowId),
        typeOverrides: deleteRowCells(next.typeOverrides, rowId),
        cellHeights: deleteRowCells(next.cellHeights, rowId),
      };
    }
    case "setTypeOverride": {
      const { rowId, column, displayType } = action;
      return patch(state, {
        typeOverrides: displayType
          ? setCell(state.typeOverrides, rowId, column, displayType)
          : deleteCell(state.typeOverrides, rowId, column),
      });
    }
    case "setCellHeight": {
      const { rowId, column, height } = action;
      // A single-line height is the default rendering.
      return patch(state, {
        cellHeights:
          height <= MIN_STRING_CELL_HEIGHT
            ? deleteCell(state.cellHeights, rowId, column)
            : setCell(state.cellHeights, rowId, column, height),
      });
    }
    case "restoreCellHeights":
      return patch(state, { cellHeights: action.cellHeights });
    case "discardEdits":
      return { ...INITIAL_STATE, cellHeights: state.cellHeights };
    case "commitEdits": {
      // The saved rows are the new original record: heights follow them to
      // their final positions.
      const cellHeights: CellHeights = state.rowIds
        ? new Map(
            state.rowIds.flatMap((rowId, index) => {
              const heights = state.cellHeights.get(rowId);
              return heights ? [[getOriginalRowId(index), heights]] : [];
            }),
          )
        : state.cellHeights;
      return { ...INITIAL_STATE, cellHeights };
    }
  }
};

export type UseTableBlockEditsReturn = {
  editedData: Rows;
  hasChanges: boolean;
  isCellModified: (rowIndex: number, columnKey: string) => boolean;
  updateCell: (rowIndex: number, columnKey: string, value: unknown) => void;
  addRow: () => void;
  deleteRow: (rowIndex: number) => void;
  duplicateRow: (rowIndex: number) => void;
  /** Row ids aligned with `editedData`. */
  rowIds: readonly string[];
  getRowId: (rowIndex: number) => string | undefined;
  /** Current index of a row, or -1 once it has been deleted. */
  getRowIndex: (rowId: string) => number;
  /** Id of the row at `originalIndex` in the record the session started from. */
  getOriginalRowId: (originalIndex: number) => string;
  isNewRow: (rowIndex: number) => boolean;
  updateCellById: (rowId: string, columnKey: string, value: unknown) => void;
  deleteRowById: (rowId: string) => void;
  /** Inserts before `beforeRowId`, or at the end for null. Returns the new id. */
  insertRowBefore: (
    beforeRowId: string | null,
    row: TableBlockSingleRecord,
  ) => string;
  /** Drops all edits, keeping cell heights (Cancel). */
  resetEdits: () => void;
  /** Drops all edits after the rows were saved, re-keying cell heights. */
  commitEdits: () => void;
  cellHeights: CellHeights;
  setCellHeight: (rowIndex: number, columnKey: string, height: number) => void;
  getCellHeight: (rowIndex: number, columnKey: string) => number | undefined;
  restoreCellHeights: (heights: CellHeights) => void;
  setTypeOverride: (
    rowIndex: number,
    columnKey: string,
    displayType: CellDisplayType | null,
  ) => void;
  getTypeOverride: (
    rowIndex: number,
    columnKey: string,
  ) => CellDisplayType | undefined;
};

export const useTableBlockEdits = (
  originalData: Rows,
): UseTableBlockEditsReturn => {
  const [state, dispatch] = useReducer(editsReducer, INITIAL_STATE);
  const newRowCounter = useRef(0);

  const rows = state.rows ?? originalData;
  const rowIds = useMemo(
    () => state.rowIds ?? deriveRowIds(originalData.length),
    [state.rowIds, originalData.length],
  );

  // Refs keep the index-based callbacks stable across renders.
  const originalDataRef = useRef(originalData);
  originalDataRef.current = originalData;
  const rowIdsRef = useRef(rowIds);
  rowIdsRef.current = rowIds;

  const editedData = useMemo(
    () =>
      rows.map((row, index) => {
        const rowEdits = state.cellEdits.get(rowIds[index]);
        return rowEdits ? { ...row, ...rowEdits } : row;
      }),
    [rows, rowIds, state.cellEdits],
  );

  // Deep-compare so reverted edits and cancelled-out row ops disable Save.
  const hasChanges = useMemo(
    () => !isEqual(editedData, originalData),
    [editedData, originalData],
  );

  const send = useCallback(
    (action: EditsAction) =>
      dispatch({ ...action, base: originalDataRef.current }),
    [],
  );

  const nextNewRowId = useCallback((): string => {
    newRowCounter.current += 1;
    return `${NEW_ROW_ID_PREFIX}${newRowCounter.current}`;
  }, []);

  const updateCellById = useCallback(
    (rowId: string, column: string, value: unknown) =>
      send({ type: "updateCell", rowId, column, value }),
    [send],
  );

  const deleteRowById = useCallback(
    (rowId: string) => send({ type: "deleteRow", rowId }),
    [send],
  );

  const insertRowBefore = useCallback(
    (beforeRowId: string | null, row: TableBlockSingleRecord): string => {
      const rowId = nextNewRowId();
      send({ type: "insertRow", beforeRowId, rowId, row });
      return rowId;
    },
    [send, nextNewRowId],
  );

  const updateCell = useCallback(
    (rowIndex: number, column: string, value: unknown) => {
      const rowId = rowIdsRef.current[rowIndex];
      if (rowId) updateCellById(rowId, column, value);
    },
    [updateCellById],
  );

  const addRow = useCallback(
    () => send({ type: "addRow", rowId: nextNewRowId() }),
    [send, nextNewRowId],
  );

  const deleteRow = useCallback(
    (rowIndex: number) => {
      const rowId = rowIdsRef.current[rowIndex];
      if (rowId) deleteRowById(rowId);
    },
    [deleteRowById],
  );

  const duplicateRow = useCallback(
    (rowIndex: number) => {
      const sourceRowId = rowIdsRef.current[rowIndex];
      if (sourceRowId) {
        send({ type: "duplicateRow", sourceRowId, rowId: nextNewRowId() });
      }
    },
    [send, nextNewRowId],
  );

  const setTypeOverride = useCallback(
    (rowIndex: number, column: string, displayType: CellDisplayType | null) => {
      const rowId = rowIdsRef.current[rowIndex];
      if (rowId) send({ type: "setTypeOverride", rowId, column, displayType });
    },
    [send],
  );

  const setCellHeight = useCallback(
    (rowIndex: number, column: string, height: number) => {
      const rowId = rowIdsRef.current[rowIndex];
      if (rowId) send({ type: "setCellHeight", rowId, column, height });
    },
    [send],
  );

  const restoreCellHeights = useCallback(
    (cellHeights: CellHeights) =>
      send({ type: "restoreCellHeights", cellHeights }),
    [send],
  );

  const resetEdits = useCallback(() => send({ type: "discardEdits" }), [send]);
  const commitEdits = useCallback(() => send({ type: "commitEdits" }), [send]);

  const getRowId = useCallback(
    (rowIndex: number): string | undefined => rowIds[rowIndex],
    [rowIds],
  );
  const getRowIndex = useCallback(
    (rowId: string): number => rowIds.indexOf(rowId),
    [rowIds],
  );
  const isNewRow = useCallback(
    (rowIndex: number): boolean => isNewRowId(rowIds[rowIndex]),
    [rowIds],
  );

  const isCellModified = useCallback(
    (rowIndex: number, column: string): boolean =>
      hasCell(state.cellEdits, rowIds[rowIndex] ?? "", column),
    [state.cellEdits, rowIds],
  );
  const getTypeOverride = useCallback(
    (rowIndex: number, column: string): CellDisplayType | undefined =>
      getCell(state.typeOverrides, rowIds[rowIndex] ?? "", column),
    [state.typeOverrides, rowIds],
  );
  const getCellHeight = useCallback(
    (rowIndex: number, column: string): number | undefined =>
      getCell(state.cellHeights, rowIds[rowIndex] ?? "", column),
    [state.cellHeights, rowIds],
  );

  return {
    editedData,
    hasChanges,
    isCellModified,
    updateCell,
    addRow,
    deleteRow,
    duplicateRow,
    rowIds,
    getRowId,
    getRowIndex,
    getOriginalRowId,
    isNewRow,
    updateCellById,
    deleteRowById,
    insertRowBefore,
    resetEdits,
    commitEdits,
    cellHeights: state.cellHeights,
    setCellHeight,
    getCellHeight,
    restoreCellHeights,
    setTypeOverride,
    getTypeOverride,
  };
};
